import type { OrionIqEventName, OrionIqFeatureName } from '@logz-pkg/analytics';
import { orionIqAnalyticsApiService, authService, Env } from '@logz-pkg/frontend-services';
import { cloneDeep } from 'lodash';
import {
  enrichEventData,
  getAccountProps,
  getIsAccountDisabled,
  getUserProps,
  isUserAccountIdentified,
  ORION_IQ_ANALYTICS_IDENTIFY_CACHE_KEY,
} from './utils';

type UserProps = Awaited<ReturnType<typeof getUserProps>>;

const formatOrionIqEventName = (featureName: string, eventName: string): string =>
  featureName === 'Global' ? eventName : `${eventName} -in- ${featureName}`;

const removeUserPii = (userProps: UserProps): UserProps => {
  const clonedUserProps = cloneDeep(userProps);

  if ('username' in clonedUserProps) {
    delete clonedUserProps.username;
  }

  return clonedUserProps;
};

const identifyUserAccount = async (): Promise<void> => {
  const userProps = removeUserPii(await getUserProps());
  const accountProps = getAccountProps();

  if (Env.configs.captureAnalytics === true) {
    await orionIqAnalyticsApiService.identifyUser(userProps);
    await orionIqAnalyticsApiService.identifyAccount(accountProps);
  }

  if (Env.configs.logToConsole === true) {
    console.log('OrionIq Analytics user account identified:', { userProps, accountProps });
  }
};

// A manual latch rather than lodash `once`, so a logout can reset it — `once` would also cache a rejected or
// empty-session identify for the life of the tab.
let identifyPromise: Promise<void> | undefined;

const ensureUserAccountIdentified = (): Promise<void> | undefined => {
  if (identifyPromise || isUserAccountIdentified(ORION_IQ_ANALYTICS_IDENTIFY_CACHE_KEY)) return identifyPromise;

  identifyPromise = identifyUserAccount().catch(error => {
    identifyPromise = undefined;

    throw error;
  });

  return identifyPromise;
};

const resetIdentification = (): void => {
  identifyPromise = undefined;
};

const capture = async <F extends OrionIqFeatureName, E extends OrionIqEventName<F>>(
  featureName: F,
  eventName: E,
  eventProps?: Record<string, unknown>,
): Promise<void> => {
  if (!authService.isAuthenticated()) return;

  const isAccountDisabled = await getIsAccountDisabled();

  if (isAccountDisabled) return;

  await ensureUserAccountIdentified();

  const eventData = enrichEventData(featureName as 'Global', eventProps ?? {});

  if (Env.configs.captureAnalytics === true) {
    orionIqAnalyticsApiService.capture(featureName, eventName, eventData);
  }

  if (Env.configs.logToConsole === true) {
    console.log('OrionIq Analytics event captured:', formatOrionIqEventName(featureName, eventName), eventData);
  }
};

export const orionIqAnalyticsService = { capture, identifyUserAccount, resetIdentification };
